import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { whyNotLoaded } from '../utils/reportError';
import api from '../services/api';
import { ListFooter } from '../ui';

// Finance · Module 2 — Payables & Receivables Aging. Reads existing invoice tables.
const inr = (v) => '₹' + Math.round(Number(v) || 0).toLocaleString('en-IN');
const input = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const BUCKETS = [
  { k: 'not_due', label: 'Not due', c: '#0E7490' },
  { k: '0_30', label: '0–30', c: '#854F0B' },
  { k: '31_60', label: '31–60', c: '#9A3412' },
  { k: '61_90', label: '61–90', c: '#B91C1C' },
  { k: '90_plus', label: '90+ overdue', c: '#7F1D1D' },
];
const BASIS = { stored: 'invoice due date', term: 'vendor/buyer term', blueprint: 'blueprint default' };

export default function AgingTab() {
  const [companies, setCompanies] = useState([]); const [canSwitch, setCanSwitch] = useState(false); const [company, setCompany] = useState('');
  const [side, setSide] = useState('payables');   // payables | receivables
  const [rows, setRows] = useState([]); const [summary, setSummary] = useState(null);
  const [unalloc, setUnalloc] = useState({});   // payables only: cleared cheques no bill records, by vendor
  const [pay, setPay] = useState(null);   // invoice being paid
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);
  // A failed read is shown as a failure, never as "No open payables for this company" (3 Oct 2026).
  const [companyErr, setCompanyErr] = useState(''); const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    api.get('/finance/daybook/companies').then(r => { setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch); setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); }).catch(e => setCompanyErr(whyNotLoaded(e)));
  }, []);

  const load = useCallback(() => {
    if (!company) return;
    setLoadErr('');
    api.get('/finance/aging/' + side, H()).then(r => { setRows(r.data.data || []); setSummary(r.data.summary); setUnalloc(r.data.unallocated_cheques_by_party || {}); })
      .catch(e => { setRows([]); setSummary(null); setUnalloc({}); setLoadErr(whyNotLoaded(e)); });
  }, [company, side, H]);
  useEffect(() => { load(); }, [load]);

  const isRecv = side === 'receivables';
  const payTable = isRecv ? 'sales-invoices' : 'purchase-invoices';
  const partyLabel = isRecv ? 'Buyer' : 'Vendor';

  const exportXlsx = () => {
    const data = rows.map(r => ({
      Invoice: r.invoice_no, ...(isRecv ? { 'Busy Inv. No.': r.busy_invoice_no || '' } : {}),
      [partyLabel]: r.party || '', GPO: r.gpo_no || '', 'Invoice Date': r.invoice_date,
      Terms: r.payment_terms || '', 'Due Date': r.due_date, 'Due basis': BASIS[r.due_basis] || r.due_basis,
      Outstanding: r.outstanding, Bucket: (BUCKETS.find(b => b.k === r.bucket) || {}).label, 'Days overdue': r.days_overdue, Status: r.payment_status,
    }));
    const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, side);
    XLSX.writeFile(wb, `khyati_${side}_aging_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Payables &amp; Receivables Aging</div>
          <div style={{ fontSize: 11, color: '#888' }}>Open invoices bucketed by due date · terms from vendor/buyer, blueprint fallback when blank</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={exportXlsx} style={{ ...input, background: '#166534', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>⬇ Export</button>
          <span style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Company</span>
          <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)} style={{ ...input, fontWeight: 700, color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Payables / Receivables toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {[['payables', '📤 Payables (we owe)'], ['receivables', '📥 Receivables (owed to us)']].map(([k, l]) => (
          <button key={k} onClick={() => setSide(k)} style={{ padding: '8px 16px', border: '1px solid ' + (side === k ? '#3B3A8F' : '#ddd'), background: side === k ? '#3B3A8F' : '#fff', color: side === k ? '#fff' : '#555', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>{l}</button>
        ))}
      </div>

      {/* Aging bucket summary */}
      {summary && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ background: '#1a1a2e', color: '#fff', borderRadius: 8, padding: '10px 16px', minWidth: 150 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', opacity: 0.7 }}>Total Outstanding</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{inr(summary.total_outstanding)}</div>
            <div style={{ fontSize: 10, opacity: 0.7 }}>{summary.count} open invoice(s)</div>
          </div>
          {/* A WRONG NUMBER ON A SCREEN READ EVERY DAY IS WORSE THAN A KNOWN GAP (Rajan, 8 Oct 2026). No cheque has
              ever been allocated to a bill, so this total does not know what the bank has paid. */}
          {!isRecv && summary.unallocated_cheques && summary.unallocated_cheques.amount > 0 && (
            <div style={{ background: '#FFF7E6', border: '1px solid #F5D9A8', borderRadius: 8, padding: '10px 16px', minWidth: 220, maxWidth: 360 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#92400E' }}>Cheques not allocated</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#92400E' }}>{inr(summary.unallocated_cheques.amount)}</div>
              <div style={{ fontSize: 10, color: '#92400E' }}>{summary.unallocated_cheques.cheques} cleared cheque(s) to {summary.unallocated_cheques.vendors} of these vendors that no bill records. The total does not subtract them.</div>
            </div>)}
          {BUCKETS.map(b => (
            <div key={b.k} style={{ background: '#fff', border: '1px solid #E8E7E7', borderTop: '3px solid ' + b.c, borderRadius: 8, padding: '10px 16px', minWidth: 110 }}>
              <div style={{ fontSize: 10, color: b.c, fontWeight: 700, textTransform: 'uppercase' }}>{b.label}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#222' }}>{inr(summary.buckets[b.k])}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            <th style={th}>Invoice</th>{isRecv && <th style={th}>Busy Inv. No.</th>}<th style={th}>{partyLabel}</th><th style={th}>GPO</th>
            <th style={th}>Inv Date</th><th style={th}>Terms</th><th style={th}>Due Date</th>
            <th style={{ ...th, textAlign: 'right' }}>Outstanding</th><th style={th}>Aging</th><th style={{ ...th, textAlign: 'center' }}>Action</th>
          </tr></thead>
          <tbody>
            {(companyErr || loadErr) ? <tr><td colSpan={isRecv ? 10 : 9} style={{ padding: 24, textAlign: 'center', color: '#A32D2D' }}>
                {companyErr ? `Could not load the list of books (${companyErr}), so no ${side} were asked for. This is not an empty list.`
                            : `Could not load the ${side} (${loadErr}). This is not an empty list.`}</td></tr>
            : !company ? <tr><td colSpan={isRecv ? 10 : 9} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>
            : rows.length === 0 ? <tr><td colSpan={isRecv ? 10 : 9} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No open {side} for this company</td></tr>
            : rows.map(r => {
              const b = BUCKETS.find(x => x.k === r.bucket) || {};
              const overdue = r.bucket !== 'not_due';
              return (
                <tr key={r.id} style={{ background: r.bucket === '90_plus' ? '#FFF5F5' : 'transparent' }}>
                  <td style={{ ...td, fontWeight: 700, color: '#3B3A8F' }}>{r.invoice_no}</td>
                  {isRecv && <td style={{ ...td, fontFamily: 'monospace', color: '#666' }}>{r.busy_invoice_no || <span style={{ color: '#bbb' }}>—</span>}</td>}
                  <td style={td}>{r.party || '—'}
                    {!isRecv && unalloc[String(r.party || '').trim().toUpperCase()] && (
                      <div style={{ fontSize: 10, color: '#92400E' }}>Cheques not allocated: {inr(unalloc[String(r.party || '').trim().toUpperCase()].amount)}</div>)}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#666' }}>{r.gpo_no || '—'}</td>
                  <td style={td}>{r.invoice_date || '—'}</td>
                  <td style={td}>{r.payment_terms || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td style={td}>{r.due_date}<span title={BASIS[r.due_basis]} style={{ marginLeft: 5, fontSize: 9, padding: '1px 5px', borderRadius: 4, background: r.due_basis === 'blueprint' ? '#FAEEDA' : r.due_basis === 'term' ? '#EEEDF8' : '#eef7ee', color: '#666' }}>{r.due_basis}</span></td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{inr(r.outstanding)}</td>
                  <td style={td}><span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: overdue ? b.c : '#e2f0ef', color: overdue ? '#fff' : '#0E7490' }}>{b.label}{overdue ? ` · ${r.days_overdue}d` : ''}</span></td>
                  {/* DOOR B IS CLOSED FOR VENDOR BILLS (3 Oct 2026): a payable is raised as a release from
                      Finance → Purchase invoices and paid from Requests → To pay. Receipts stay here. */}
                  <td style={{ ...td, textAlign: 'center' }}>{isRecv
                    ? <button onClick={() => setPay(r)} style={{ padding: '4px 10px', border: 'none', borderRadius: 5, background: '#166534', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Receive</button>
                    : <span title="Raise a release on the bill (Finance → Purchase invoices); it is paid from Requests → To pay" style={{ fontSize: 10, color: '#888' }}>via release</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ListFooter shown={rows.length} total={rows.length} noun="invoices" />

      {pay && <PayModal inv={pay} isRecv={isRecv} table={payTable} H={H} onClose={() => setPay(null)} onDone={() => { setPay(null); load(); }} />}
    </div>
  );
}

function PayModal({ inv, isRecv, table, H, onClose, onDone }) {
  const [amount, setAmount] = useState(inv.outstanding);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [err, setErr] = useState(''); const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await api.post(`/finance/${table}/${inv.id}/payment`, { amount: Number(amount), date, reference }, H());
      onDone();
    } catch (e2) { setErr(e2.response?.data?.error || 'Failed — payments may need management rights'); setSaving(false); }
  };
  const lab = { fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 380 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{isRecv ? 'Record Receipt' : 'Record Payment'}</h3>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>{inv.invoice_no} · {inv.party} · outstanding {inr(inv.outstanding)}</div>
        <div style={{ marginBottom: 10 }}><label style={lab}>Amount</label><input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={{ ...input, width: '100%' }} required /></div>
        <div style={{ marginBottom: 10 }}><label style={lab}>Date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...input, width: '100%' }} /></div>
        <div style={{ marginBottom: 14 }}><label style={lab}>Reference (cheque/UTR)</label><input value={reference} onChange={e => setReference(e.target.value)} style={{ ...input, width: '100%' }} /></div>
        {err && <div style={{ color: '#A32D2D', fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ ...input, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...input, background: '#3B3A8F', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
